import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toggleLanguage } from "../Redux/languageActions";
import WhatsAppAnimation from "./../Utils/WhatsAppAnimation";
import { UseGA } from "./../Utils/UseGA";
import NotificationBell from "./Notifications/NotificationBell";
import { Globe, Download } from "lucide-react";
import {
  requestNotificationPermission,
  shouldShowNotificationPrompt,
  recordNotificationPromptShown,
  refreshFCMToken,
} from "../firebase";

const Navbar = () => {
  const cartItems = useSelector((state) => state.bhogCart?.items || []);
  const dispatch = useDispatch();
  const language = useSelector((state) => state.language.language);
  const arr = ["हि", "En"];
  const [totalItem, setTotalItem] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const user = useSelector((state) => state.user.user);
  const token = useSelector((state) => state.user.token);
  const swayamCreator = useSelector((state) => state.swayamCreator?.creator);

  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showNotifPrompt, setShowNotifPrompt] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  const handleToggleLanguage = () => {
    try {
      const searchParams = new URLSearchParams(location.search);
      const hasLangParam =
        searchParams.has("lang") ||
        searchParams.has("language") ||
        searchParams.has("hindi") ||
        searchParams.has("english") ||
        searchParams.has("hl");

      if (hasLangParam) {
        searchParams.delete("lang");
        searchParams.delete("language");
        searchParams.delete("hindi");
        searchParams.delete("english");
        searchParams.delete("hl");

        const newSearch = searchParams.toString();
        navigate(
          {
            pathname: location.pathname,
            search: newSearch ? `?${newSearch}` : "",
          },
          { replace: true },
        );
      }
    } catch (e) {
      console.error("Language toggle error:", e);
    }
    dispatch(toggleLanguage());
  };

  useEffect(() => {
    setTotalItem(cartItems.length);
  }, [cartItems]);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 20) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (user && token) {
      setTimeout(() => {
        if ("Notification" in window) {
          if (shouldShowNotificationPrompt()) {
            setShowNotifPrompt(true);
            recordNotificationPromptShown();
          } else if (Notification.permission === "granted") {
            refreshFCMToken(token);
          }
        }
      }, 3000);

      const handleVisibilityChange = () => {
        if (
          !document.hidden &&
          "Notification" in window &&
          Notification.permission === "granted"
        ) {
          refreshFCMToken(token);
        }
      };
      document.addEventListener("visibilitychange", handleVisibilityChange);

      return () => {
        document.removeEventListener(
          "visibilitychange",
          handleVisibilityChange,
        );
      };
    }
  }, [user, token]);

  const handleEnableNotifications = () => {
    setShowNotifPrompt(false);
    requestNotificationPermission(token);
  };

  useEffect(() => {
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    return () => {
      window.removeEventListener(
        "beforeinstallprompt",
        handleBeforeInstallPrompt,
      );
    };
  }, []);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === "accepted") {
        setDeferredPrompt(null);
      }
    }
  };

  const scrollToSection = (sectionId) => {
    let attempts = 0;
    const tryScroll = () => {
      const section = document.getElementById(sectionId);
      if (section) {
        section.scrollIntoView({ behavior: "smooth" });
      } else if (attempts < 5) {
        attempts++;
        setTimeout(tryScroll, 100);
      }
    };
    setTimeout(tryScroll, 100);
  };

  const handleClicks = (e, sectionId) => {
    e.preventDefault();
    if (window.location.pathname !== "/") {
      navigate("/", { state: { scrollTo: sectionId } });
    } else {
      scrollToSection(sectionId);
    }
  };

  const handleClicksHome = (e) => {
    e.preventDefault();
    window.scrollTo({ top: 0, behavior: "smooth" });
    navigate("/");
  };

  useEffect(() => {
    if (location.pathname === "/" && location.state?.scrollTo) {
      scrollToSection(location.state.scrollTo);
      navigate("/", { replace: true, state: {} });
    }
  }, [location, navigate]);

  const { trackEvent } = UseGA();

  if (
    location.pathname === "/map-routes" ||
    location.pathname === "/map" ||
    location.pathname.startsWith("/map-routes") ||
    location.pathname.startsWith("/map")
  ) {
    return null;
  }

  return (
    <>
      <nav className="fixed w-full top-0 z-50 transition-all duration-300 shadow-[0_2px_4px_rgba(0,0,0,0.1)] font-semibold bg-white">
        <div className="max-w-7xl mx-auto px-4 xs:px-6 pt-2 pb-1.5 sm:px-10 lg:px-8">
          <div className="flex justify-between items-center h-10 sm:h-12">
            <div className="flex items-center flex-shrink-0">
              <Link
                to="/"
                onClick={handleClicksHome}
                className="transition-transform duration-300 hover:scale-105 block"
              >
                <img
                  src="/Images/logo.svg"
                  alt="ParamBhog Logo"
                  className="w-28 sm:w-36 min-w-[50px] aspect-[144/40]"
                  width="144"
                  height="40"
                />
              </Link>
            </div>

            <div className="hidden md:flex items-center space-x-8 text-base font-semibold text-[#320B3F]">
              <Link
                onClick={handleClicksHome}
                className="relative py-2 hover:text-[#FF7700] transition-colors duration-300 group cursor-pointer"
              >
                Home
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#FF7700] transition-all duration-300 group-hover:w-full"></span>
              </Link>
              <Link
                onClick={(e) => handleClicks(e, "about-us")}
                className="relative py-2 hover:text-[#FF7700] transition-colors duration-300 group cursor-pointer"
              >
                About Us
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#FF7700] transition-all duration-300 group-hover:w-full"></span>
              </Link>
              <Link
                onClick={(e) => handleClicks(e, "feedback")}
                className="relative py-2 hover:text-[#FF7700] transition-colors duration-300 group cursor-pointer"
              >
                Feedback
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#FF7700] transition-all duration-300 group-hover:w-full"></span>
              </Link>
            </div>

            <div className="flex items-center gap-3 sm:gap-4">
              <button
                onClick={handleToggleLanguage}
                className="flex items-center gap-1 px-2 py-1 sm:px-3 sm:py-1.5 rounded-full border border-vlight hover:border-[#FF7700]/50 hover:bg-orange-50/50 transition-all duration-300 text-xs sm:text-sm font-medium text-vheavy hover:text-[#FF7700]"
              >
                <Globe className="w-3.5 h-3.5 text-[#FF7700]" />
                <span>{arr[language]}</span>
              </button>

              {/* {deferredPrompt && (
              <button
                onClick={handleInstallClick}
                className="flex items-center justify-center p-1.5 sm:px-3 sm:py-1.5 bg-[#FF7700] hover:bg-orange-600 text-white rounded-full transition-all duration-250 hover:scale-105"
                aria-label="Install App"
              >
                <Download className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                <span className="hidden sm:inline sm:ml-1 text-xs font-bold">
                  Install App
                </span>
              </button>
            )} */}
              {/* 
            {user && (
              <div className="flex items-center">
                <NotificationBell />
              </div>
            )} */}

              {/* Shopping Cart */}
              {!swayamCreator && (
                <Link
                  to="/bhog-cart"
                  className="relative p-1.5 rounded-full hover:bg-gray-100 duration-200 text-[#320B3F] hover:text-[#FF7700] hover:scale-105 transition-all block"
                >
                  <img
                    src="/Images/cart.svg"
                    alt="Shopping Cart"
                    className="h-5 w-5 sm:h-6 sm:w-6 min-w-[12px] min-h-[12px]"
                    loading="lazy"
                  />
                  {totalItem > 0 && (
                    <span className="absolute -top-1 -right-1 bg-[#FF7700] text-white rounded-full w-4 h-4 flex items-center justify-center text-[9px] font-bold ring-2 ring-white">
                      {totalItem}
                    </span>
                  )}
                </Link>
              )}

              <button
                onClick={() => {
                  trackEvent(
                    "User",
                    "Dashboard",
                    "Check Dashboard form Navbar",
                    1,
                  );
                  navigate(
                    swayamCreator ? "/swayam-creator/dashboard" : "/dashboard",
                  );
                }}
                className="rounded-full hover:bg-gray-100 transition-all duration-200 text-[#320B3F] hover:text-[#FF7700] hover:scale-105"
                aria-label="User Dashboard"
              >
                <img
                  src="/Images/dashboard.svg"
                  alt="User Dashboard"
                  className="h-6 w-6 sm:h-6 sm:w-6 min-w-[12px] min-h-[12px]"
                  width="24"
                  height="24"
                />
              </button>
            </div>
          </div>
        </div>
      </nav>

      <WhatsAppAnimation />

      {showNotifPrompt && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 w-[90%] max-w-sm bg-white rounded-2xl shadow-[0_4px_10px_rgba(0,0,0,0.25)] border border-gray-100 p-4 z-[100] transition-all duration-300">
          <div className="flex gap-3 mb-3">
            <div className="w-12 h-12 rounded-full bg-[#FFF3E7] flex items-center justify-center flex-shrink-0">
              <img
                className="w-5 h-5 object-contain"
                src="Images/Home/Notification.svg"
                alt="Notification"
              />
            </div>

            <div>
              <p className="font-medium text-lg text-[#320B3F]">
                Allow Notifications
              </p>
              <p className="text-xs mt-1 text-[#99859F]">
                Get live updates about your arjee, bookings and temple crowd.
              </p>
            </div>
          </div>

          <div className="flex gap-2 justify-end border-t border-[#EBEBEB] pt-3 mt-3">
            <button
              onClick={() => setShowNotifPrompt(false)}
              className="px-4 py-2 text-xs font-semibold text-[#A290A8] rounded-xl hover:bg-gray-100 transition-colors"
            >
              Maybe Later
            </button>

            <button
              onClick={handleEnableNotifications}
              className="px-4 py-2 text-xs font-semibold text-white bg-[#FF7700] hover:bg-orange-600 rounded-lg shadow-shadow4 transition-colors"
            >
              Enable
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;
